import { useState, useCallback, useRef } from 'react'
import { useIsFetching, useQueryClient } from 'react-query'
import { Link, useNavigate } from 'react-router-dom'
import { FieldErrors, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ErrorMessage } from '@hookform/error-message'
import { parseISO } from 'date-fns'
import { Switch } from '@headlessui/react'
import {
  ArrowCounterClockwise,
  CircleNotch,
  ListPlus,
  PaperPlaneTilt,
  Storefront,
} from '@phosphor-icons/react'

import { formatPhoneNumber } from '@/cart/utils/formatPhoneNumber'

import { useAppDispatch, useAppSelector } from '@/main/app/hooks/storeHooks'
import {
  TDates,
  TProfile,
  TBilling,
  TShipping,
  Submission,
  SubmissionSchema,
} from '@/main/app/schema'

import { submitList, FormState, Rejected } from '@/main/features/List/listSlice'

import { selectItemsByListId } from '@/main/features/Item/itemSlice'
import { compareHistory } from '@/main/features/Item/utils/compareHistory'

import { Button } from '@/main/components/Button/Button'
import { useListOutletContext } from '@/cart/hooks/useListOutletContext'
import { formatLongDate } from '@/main/app/utils/dates'
import { DeliveryValues } from '../DeliveryValues/DeliveryValues'
import { ListResetDialog } from '@/main/features/List/components/ListDialogs/ListResetDialog/ListResetDialog'
import { useAppContext } from '@/main/app/context'
import { useListHistory } from '@/main/features/List/hooks/useListHistory'

import { fieldsetClassList, labelClassList, legendClassList } from '@/main/app/utils/classNames'
import { SubmissionFormError } from './SubmissionFormError'

type KeysOfUnion<T> = T extends T ? keyof T : never

type ProfileKey = `profile.${keyof TProfile}`
type BillingKey = `billing.${keyof TBilling}`
type DateKey = `dates.${keyof TDates}`
type ShippingKey = `shipping.${KeysOfUnion<TShipping>}`

const FORM_ERROR = 'FORM_ERROR' as const

type FormErrors = { [FORM_ERROR]?: never }

function isRejectedValue(r: unknown): r is Rejected {
  return (
    (r as Rejected).name !== undefined &&
    ['unknown', 'invalid', 'response', 'request', 'axios'].some(
      (name) => name === (r as Rejected).name
    )
  )
}

function isFieldError(v: unknown): v is Partial<FieldErrors<Submission & FormErrors>> {
  const keys = typeof v === 'object' && v !== null && Object.keys(v)

  return (
    keys &&
    keys.some((key) =>
      ['profile', 'billing', 'dates', 'shipping', 'optional', 'items'].some(
        (field) => field.indexOf(key) > -1
      )
    )
  )
}

const reduceFieldKeys = ({ profile, billing, dates, shipping }: FormState) => {
  const profileKeys = profile?.values
    ? Object.keys(profile.values).map(
        (key) => `profile.${key as keyof typeof profile}` as ProfileKey
      )
    : []

  const billingKeys = billing?.values
    ? Object.keys(billing.values).map(
        (key) => `billing.${key as keyof typeof billing}` as BillingKey
      )
    : []

  const dateKeys = dates?.values
    ? Object.keys(dates.values).map((key) => `dates.${key as keyof typeof dates}` as DateKey)
    : []

  const shippingKeys = shipping?.values
    ? Object.keys(shipping.values).map(
        (key) => `shipping.${key as keyof typeof shipping}` as ShippingKey
      )
    : []

  return [...profileKeys, ...billingKeys, ...dateKeys, ...shippingKeys].filter(Boolean)
}

const fieldNames = {
  profile: 'Contact Info',
  firstName: 'Contact Info > First Name',
  lastName: 'Contact Info > Last Name',
  email: 'Contact Info > Email',
  phone: 'Contact Info > Phone Number',
  billing: 'Project/Billing Info',
  client: 'Project/Billing > Client Name',
  reference: 'Project/Billing > Job/Project Reference',
  cc: 'Project/Billing > Additional Email (CC)',
  dates: 'Rental Dates',
  startDate: 'Rental Dates > Starting Date',
  endDate: 'Rental Dates > Ending/Return Date',
  shipping: 'Pick-up/Delivery Info',
  shippingMethod: 'Pick-up or Delivery',
  deliveryType: 'Delivery Options',
  startTime: 'Time Window for Delivery',
  endTime: 'Time Window for Return',
  deliveryContact: 'Delivery Contact Name',
  deliveryPhone: 'Delivery Contact Phone Number',
  deliveryBusiness: 'Location Name for Delivery',
  deliveryAddress: 'Delivery Address',
  deliveryUnit: 'Suite/Floor/Unit for Delivery',
  deliveryInstructions: 'Delivery Instructions',
  deliveryCity: 'City/Borough for Delivery',
  deliveryZip: 'ZIP/Postal Code for Delivery',
  returnContact: 'Return Contact Name',
  returnPhone: 'Return Contact Phone Number',
  returnBusiness: 'Location Name for Return Pick-Up',
  returnAddress: 'Address for Return Pick-Up',
  returnUnit: 'Suite/Floor/Unit for Return Pick-Up',
  returnInstructions: 'Return Instructions',
  returnCity: 'City/Borough for Return Pick-Up',
  returnZip: 'ZIP/Postal Code for Return Pick-Up',
  optional: 'Optional notes',
}

export function SubmissionForm() {
  const [showNotes, setShowNotes] = useState(false)
  const [optIn, setOptIn] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const errRef = useRef<HTMLDivElement>(null)
  const submitRef = useRef<HTMLDivElement>(null)
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  const { id, list, form } = useListOutletContext()

  const {
    formPaths: [step1, step2],
  } = useAppContext()

  const items = useAppSelector((state) => selectItemsByListId(state, id))
  const isReady = useAppSelector((state) => state.app.isReady)

  const dispatch = useAppDispatch()
  const client = useQueryClient()
  const isFetching = useIsFetching()
  const navigate = useNavigate()

  const listHistory = useListHistory(list, items)

  const {
    register,
    handleSubmit,
    setError,
    formState: { isValid, isSubmitting, isValidating, isSubmitted, isSubmitSuccessful, errors },
  } = useForm<Submission & FormErrors>({
    defaultValues: {
      profile: form?.profile?.values,
      billing: form?.billing?.values,
      dates: form?.dates?.values,
      shipping: form?.shipping?.values,
    },
    resolver: zodResolver(SubmissionSchema),
  })

  const startsOn = form?.dates?.values.startDate
    ? formatLongDate(parseISO(form.dates?.values.startDate))
    : undefined
  const endsOn = form?.dates?.values.endDate
    ? formatLongDate(parseISO(form.dates?.values.endDate))
    : undefined
  const fieldKeys = form && reduceFieldKeys(form)

  const disableSubmission =
    isSubmitSuccessful ||
    !isReady ||
    !items.length ||
    isFetching > 0 ||
    isSubmitting ||
    isValidating

  const onError = useCallback(
    (err: typeof errors | unknown, _e?: unknown) => {
      errRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })

      if (isRejectedValue(err)) {
        if (err.name === 'invalid') {
          err.errors.forEach((error) => {
            const group = error.idref?.replace('_', '.') as
              | ProfileKey
              | BillingKey
              | ShippingKey
              | DateKey
              | undefined

            if (group && error.field !== 'items') {
              setError(group, {
                message: `Please check ${fieldNames[error.field] ?? error.field} for errors. ${
                  error.message
                } The field may also need to be re-entered or re-selected. Remember to click the "Save" button once you have made any changes.`,
              })
            } else if (error.field === 'items') {
              setError(FORM_ERROR, { type: 'Cart', message: error.message })
            }
          })
        } else if (err.name === 'response') {
          setError(FORM_ERROR, { type: 'Server', message: err.message })
        }
      } else if (!isFieldError(err)) {
        console.error(err)

        const errorMessage =
          err instanceof Error ? err.message || err.stack : 'Unknown error (see console)'

        const dataError = err instanceof Error && err.cause === 'Invalid data'

        if (dataError) {
          setError('root.preflight', { type: 'Data', message: errorMessage })
        } else {
          setError(FORM_ERROR, { type: 'Unknown', message: errorMessage })
        }
      }
    },
    [setError]
  )

  const onSubmit = useCallback(
    async (values: Submission) => {
      try {
        if (disableSubmission) throw new Error('Form is disabled', { cause: 'Invalid data' })
        if (!form) throw new Error('Form not found', { cause: 'Invalid data' })
        if (!items.length) throw new Error('Cart is empty', { cause: 'Invalid data' })

        const data = items.map((item) => {
          const query = client.getQueryData<AcmeProp>(['item', `${item.postId}`])

          if (!query)
            throw new Error(
              'Cart is not loaded or contains errors.\nPlease review props in cart.',
              {
                cause: 'Invalid data',
              }
            )

          // this order in spreading the object is important
          // the payload id sent to the server MUST be an integer
          // if the id is a string the submission will be rejected as invalid
          // `item` is of type `Item` whose "id" property is of type `EntityId` - which can be a string type
          // `query` will be type `AcmeProp` which will always have an integer as its "id"
          // spreading `query` last ensures its "id" property overwrites the "id" property of `item`

          return { ...item, ...query }
        })

        const history =
          values.optional.isUpdate && list.history
            ? compareHistory(values, data, list.history)
            : undefined

        // const recordId = values.optional.isUpdate ? list.recordId : undefined

        const results = await dispatch(
          submitList({ values, data, history, optIn, listId: id, requestId: list.requestId })
        ).unwrap()

        navigate(`/${id}/form/success?id=${results.hash}&optIn=${results.optIn}`)
      } catch (err) {
        onError(err)
      }
    },
    [
      disableSubmission,
      form,
      items,
      list.history,
      list.requestId,
      dispatch,
      id,
      navigate,
      client,
      optIn,
      onError,
    ]
  )

  const handleScrollToSubmit = () => {
    submitRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => submitButtonRef.current?.focus(), 600)
  }

  const formHistory = listHistory
    ? (Object.entries(listHistory.formHistory).filter(([, val]) => val !== false) as [
        string,
        string[]
      ][])
    : false

  return (
    <>
      {isSubmitting && (
        <div
          className="twc-fixed twc-z-[99] twc-rounded twc-inset-0 twc-bg-slate-900/50 twc-flex twc-justify-center twc-items-center"
          aria-hidden="true"
        >
          <div className="twc-relative twc-flex twc-flex-col twc-gap-3 twc-items-center twc-justify-center -twc-top-32 twc-bg-slate-50 twc-pt-8 twc-pb-6 twc-px-4 twc-rounded twc-shadow-2xl dark:twc-bg-slate-800">
            <CircleNotch className="twc-animate-spin" size={48} />
            <small>
              <em>Submitting your cart</em>
            </small>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <h2 className="twc-font-medium">3. Review: &quot;{list?.name ?? 'My Cart'}&quot;</h2>
        {!listHistory ? (
          <p className="twc-prose-xl twc-mb-6 twc-p-4 twc-bg-blue-50 dark:twc-bg-blue-900/60 twc-rounded">
            To check what&apos;s available and get a quote,{' '}
            <button
              className="twc-font-bold twc-underline hover:twc-text-primary dark:hover:twc-text-orange-300"
              type="button"
              onClick={handleScrollToSubmit}
            >
              Submit the Cart
            </button>{' '}
            at the bottom of the page.
          </p>
        ) : !listHistory.delta && formHistory && formHistory.length === 0 ? (
          <p className="twc-prose-base twc-mb-6 twc-p-4 twc-bg-emerald-50 dark:twc-bg-emerald-900/60 twc-rounded twc-font-medium">
            No changes have been made to your project (last sent {listHistory.lastSent}).
          </p>
        ) : (
          <div className="twc-prose-xl twc-mb-6 twc-p-4 twc-bg-amber-100 dark:twc-bg-pink-400/20 dark:twc-text-white twc-rounded">
            <p>
              <strong>There are un-sent changes for your project.</strong>
              <br />
              {listHistory.delta > 0 && formHistory && formHistory.length > 0
                ? 'Props in your cart have changed and your rental info has changed.'
                : listHistory.delta > 0
                ? 'Props in your cart have changed.'
                : formHistory && formHistory.length > 0
                ? 'Rental info has changed.'
                : null}
            </p>
            <p>
              <button
                className="twc-font-bold twc-underline hover:twc-text-primary dark:hover:twc-text-orange-300"
                type="button"
                onClick={handleScrollToSubmit}
              >
                Submit the Cart
              </button>{' '}
              at the bottom of the page to share updates with ACME.
            </p>
          </div>
        )}

        <p>Review your project info before submitting.</p>

        <div className="twc-flex twc-flex-col md:twc-flex-row twc-gap-4 twc-items-stretch twc-justify-between twc-mb-8">
          <div className="twc-border twc-flex-1 twc-rounded twc-p-4 twc-border-gray-100 dark:twc-border-gray-800 twc-shadow twc-bg-white dark:twc-bg-slate-800/40 twc-max-w-3xl prose-h3:twc-mt-0 prose-p:twc-mt-0">
            <div className="twc-flex twc-justify-between twc-items-start">
              <h3>Contact &amp; Project</h3>
              <Link to={`/${id}/form/${step1}`}>Edit</Link>
            </div>
            <p>
              {form?.profile?.values.firstName} {form?.profile?.values.lastName},{' '}
              {formatPhoneNumber(form?.profile?.values.phone ?? '')}
              <br />
              {form?.profile?.values.email}
              <br />
              Project/Billing: {form?.billing?.values.reference}, {form?.billing?.values.client}
            </p>
          </div>

          <div className="twc-border twc-flex-1 twc-rounded twc-p-4 twc-border-gray-100 dark:twc-border-gray-800 twc-shadow twc-bg-white dark:twc-bg-slate-800/50 twc-max-w-3xl prose-h3:twc-mt-0 prose-p:twc-mt-0">
            <div className="twc-flex twc-justify-between twc-items-start">
              <h3>
                Rental Dates &amp;{' '}
                {form?.shipping?.values.shippingMethod === 'delivery' ? 'Delivery' : 'Pick-up'}
              </h3>

              <Link to={`/${id}/form/${step2}`}>Edit</Link>
            </div>

            {startsOn && endsOn && (
              <p>
                <strong className="twc-font-semibold">
                  {form?.shipping?.values.shippingMethod === 'delivery' ? 'Delivery' : 'Pick-up'}:
                </strong>{' '}
                {startsOn}
                <br />
                <strong className="twc-font-semibold">Return:</strong> {endsOn}
              </p>
            )}

            {form?.shipping?.values.shippingMethod === 'pickup' ? (
              <p>
                <strong>Pick-up at ACME&apos;s location</strong>{' '}
                <Storefront className="twc-inline" />
                <br />
                <a
                  className="hover:twc-no-underline twc-prose-base twc-font-normal"
                  href="https://g.page/AcmeBrooklyn?share"
                >
                  350 Meserole St., Building D, Brooklyn, NY 11206
                </a>
                <br />
                Between Bogart St. &amp; Morgan Ave. in Bushwick
                <br />
                <strong className="twc-font-semibold">Hours:</strong> 9 AM to 6 PM
              </p>
            ) : form?.shipping?.values.shippingMethod === 'delivery' &&
              form.shipping.values.shippingDetail ? (
              <DeliveryValues values={form.shipping.values.shippingDetail} />
            ) : null}
          </div>
        </div>

        <Switch.Group>
          <div className="twc-flex twc-items-center twc-mb-8 twc-gap-3">
            <Switch
              checked={showNotes}
              onChange={setShowNotes}
              className={`${
                showNotes
                  ? '!twc-bg-emerald-600 dark:!twc-bg-emerald-600'
                  : '!twc-bg-gray-300 dark:!twc-bg-gray-600'
              } twc-relative twc-inline-flex twc-h-6 twc-w-11 twc-items-center twc-rounded-xl twc-transition-colors`}
            >
              <span
                className={`${
                  showNotes ? 'twc-translate-x-6' : 'twc-translate-x-1'
                } twc-inline-block twc-h-4 twc-w-4 twc-rounded-full twc-bg-white twc-transform twc-transition twc-ease-in-out`}
              />
            </Switch>
            <Switch.Label className="twc-font-medium">
              I have extra notes/need props I could not find
            </Switch.Label>
          </div>
        </Switch.Group>

        {showNotes && (
          <div className="twc-flex twc-flex-col twc-gap-6 twc-mb-8">
            <div>
              <label htmlFor="notes_comments">
                Comments <small>(optional)</small>
              </label>
              <br />
              <textarea
                className="twc-form-textarea twc-w-full twc-max-w-md twc-h-24 twc-rounded dark:twc-bg-slate-800 dark:twc-border-slate-600 dark:twc-text-zinc-200"
                id="notes_comments"
                {...register('optional.comments')}
              />
            </div>

            <div>
              <label htmlFor="notes_requests">
                Find everything you need? <small>(optional)</small>
              </label>
              <p className="twc-mt-0 twc-prose-sm">
                Let us know if there are props you&apos;re looking to rent that you did not find in
                our catalog, to help our team with future acquisitions.
              </p>
              <textarea
                className="twc-form-textarea twc-w-full twc-max-w-md twc-h-24 twc-rounded dark:twc-bg-slate-800 dark:twc-border-slate-600 dark:twc-text-zinc-200"
                id="notes_requests"
                {...register('optional.requests')}
              />
            </div>
          </div>
        )}

        {fieldKeys &&
          fieldKeys.map((field) => <input key={field} type="hidden" {...register(field)} />)}

        {listHistory && formHistory && formHistory.length > 0 ? (
          <div>
            <h3>Is this a new project?</h3>
            <fieldset
              className={`${fieldsetClassList} twc-mb-8 twc-pb-4`}
              role="radiogroup"
              aria-invalid={errors.optional?.isUpdate ? 'true' : 'false'}
              aria-describedby="form-submission-feedback"
            >
              <legend className={legendClassList}>
                Confirm if this is a new project, or, an update to your current request
              </legend>

              <div className="twc-flex twc-flex-col twc-gap-4 twc-mb-2">
                <label className={`${labelClassList} twc-inline-flex twc-items-center twc-gap-2`}>
                  <input
                    className="twc-form-radio twc-peer"
                    type="radio"
                    id="no"
                    value="no"
                    {...register('optional.isUpdate')}
                  />{' '}
                  <span className="twc-text-base peer-checked:twc-font-bold peer-checked:twc-text-blue-800 dark:peer-checked:twc-text-blue-300">
                    Yes, this is a new project and I require a new quote/invoice number
                  </span>
                </label>
                <label className={`${labelClassList} twc-inline-flex twc-items-center twc-gap-2`}>
                  <input
                    className="twc-form-radio twc-peer"
                    type="radio"
                    id="yes"
                    value="yes"
                    {...register('optional.isUpdate')}
                  />{' '}
                  <span className="twc-text-base peer-checked:twc-font-bold peer-checked:twc-text-blue-800 dark:peer-checked:twc-text-blue-300">
                    No, this is an update for a currently active request
                  </span>
                </label>
              </div>
            </fieldset>

            <div className="twc-bg-sky-100/75 dark:twc-bg-sky-900 twc-px-4 twc-py-1 twc-rounded twc-my-8">
              <h4>Working on multiple projects? Create more than one cart!</h4>
              <p>
                Use the{' '}
                <span className="twc-bg-sky-200 dark:twc-text-white dark:twc-bg-sky-700 twc-py-1 twc-px-1.5 twc-rounded twc-text-sm twc-font-medium">
                  Options
                </span>{' '}
                menu at the top right of the page to{' '}
                <span className="twc-inline-flex dark:twc-text-white twc-text-sm twc-font-medium twc-items-center twc-gap-1 twc-bg-sky-200 dark:twc-bg-sky-700 twc-py-1 twc-pl-1.5 twc-pr-3 twc-rounded">
                  <ListPlus /> Create a new project
                </span>
              </p>
            </div>
          </div>
        ) : (
          <input
            type="hidden"
            {...register('optional.isUpdate', {
              value: list?.history !== undefined ? 'yes' : 'no',
            })}
          />
        )}

        <Switch.Group>
          <div className="twc-flex twc-items-center twc-mb-8 twc-gap-3">
            <Switch
              checked={optIn}
              onChange={setOptIn}
              className={`${
                optIn
                  ? '!twc-bg-emerald-600 dark:!twc-bg-emerald-600'
                  : '!twc-bg-gray-300 dark:!twc-bg-gray-600'
              } twc-relative twc-inline-flex twc-h-6 twc-w-11 twc-items-center twc-rounded-xl twc-transition-colors`}
            >
              <span
                className={`${
                  optIn ? 'twc-translate-x-6' : 'twc-translate-x-1'
                } twc-inline-block twc-h-4 twc-w-4 twc-rounded-full twc-bg-white twc-transform twc-transition twc-ease-in-out`}
              />
            </Switch>
            <Switch.Label className="twc-font-medium">
              Yes, sign up {form?.profile?.values.email ?? 'my email'} to receive the ACME Props
              Newsletter!
            </Switch.Label>
          </div>
        </Switch.Group>

        <div className="suggestion-input-field">
          <label
            className={`${labelClassList} suggestion-input-label`}
            htmlFor="optional.suggestion"
          >
            Suggestion
          </label>
          <input
            className={`twc-form-input twc-leading-relaxed twc-shadow-inner twc-rounded twc-w-full twc-border-slate-400 twc-py-1.5 dark:twc-bg-slate-800 dark:twc-border-slate-600 dark:twc-text-zinc-200 invalid:twc-border-red-600 twc-mt-1 suggestion-input-text`}
            type="text"
            aria-invalid="false"
            placeholder="Add a suggestion to improve the experience"
            tabIndex={-1}
            autoComplete="off"
            {...register('optional.suggestion')}
          />
          <div id="suggestion-feedback" aria-live="polite" />
        </div>

        <div className="twc-flex twc-flex-col twc-items-end twc-gap-6" ref={submitRef}>
          <Button
            type="submit"
            size="large"
            disabled={disableSubmission}
            ref={submitButtonRef}
            className="focus:twc-ring twc-ring-offset-2 twc-ring-blue-500 twc-font-extrabold"
          >
            Submit Cart <PaperPlaneTilt />
          </Button>

          <Button
            size="small"
            variant="ghost"
            className="twc-bg-slate-200 dark:twc-bg-slate-700 dark:hover:twc-bg-rose-600 twc-font-medium hover:twc-bg-rose-600 hover:twc-text-white"
            onClick={() => setIsDialogOpen(true)}
          >
            Start over/reset <ArrowCounterClockwise />
          </Button>
        </div>

        <div
          id="form-submission-feedback"
          className={`${
            isSubmitted && !isValid && !isSubmitting ? 'twc-opacity-100' : 'twc-opacity-0'
          } twc-transition-opacity twc-prose twc-bg-rose-100 dark:twc-bg-rose-900 twc-opa-100 twc-prose-rose dark:twc-text-white twc-p-4 twc-rounded twc-my-4 prose-pre:twc-bg-slate-400/25 prose-pre:twc-text-slate-800`}
          aria-live="polite"
          ref={errRef}
        >
          {errors?.root?.preflight && (
            <>
              <p>An error occurred when handling your submission.</p>
              <pre>{errors.root.preflight.message ?? 'Unknown error (see console)'}</pre>
            </>
          )}

          {errors[FORM_ERROR] && (
            <SubmissionFormError
              items={items}
              form={form}
              client={client}
              errorType={errors[FORM_ERROR].type}
              errorMessage={errors[FORM_ERROR].message}
            />
          )}

          {errors.profile && (
            <dl>
              <dt className="twc-font-semibold">
                There is invalid contact info. Edit the affected fields in the{' '}
                <Link to={`/${id}/form/${step1}`}>Contact Info section</Link> and try to re-submit.
              </dt>
              {Object.keys(errors.profile).map((errField) => (
                <ErrorMessage key={errField} errors={errors} name={`profile.${errField}`} as="dd" />
              ))}
            </dl>
          )}
          {errors.billing && (
            <dl>
              <dt className="twc-font-semibold">
                There is invalid billing info. Edit the affected fields in the{' '}
                <Link to={`/${id}/form/${step1}`}>Project Info section</Link> and try to re-submit.
              </dt>
              {Object.keys(errors.billing).map((errField) => (
                <ErrorMessage key={errField} errors={errors} name={`billing.${errField}`} as="dd" />
              ))}
            </dl>
          )}
          {errors.dates && (
            <dl>
              <dt className="twc-font-semibold">
                There are invalid rental dates. Edit the affected fields in the{' '}
                <Link to={`/${id}/form/${step2}`}>Rental Dates section</Link> and try to re-submit.
              </dt>
              {Object.keys(errors.dates).map((errField) => (
                <ErrorMessage key={errField} errors={errors} name={`dates.${errField}`} as="dd" />
              ))}
            </dl>
          )}
          {errors.shipping && (
            <dl>
              <dt className="twc-font-semibold">
                There is invalid pick-up/delivery info. Edit the affected fields in the{' '}
                <Link to={`/${id}/form/${step2}`}>Pick-up/Delivery Info section</Link> and try to
                re-submit.
              </dt>
              {Object.keys(errors.shipping).map((errField) =>
                errField === 'shippingDetail' ? (
                  // @ts-expect-error: extremely complicated typing required to make the compiler happy
                  Object.keys(errors.shipping.shippingDetail).map((errSubField) => (
                    <ErrorMessage
                      key={errSubField}
                      errors={errors}
                      name={`shipping.shippingDetail.${errSubField}`}
                      as="dd"
                    />
                  ))
                ) : (
                  <ErrorMessage
                    key={errField}
                    errors={errors}
                    name={`shipping.${errField}`}
                    as="dd"
                  />
                )
              )}
            </dl>
          )}
          {errors.optional && (
            <dl>
              <dt>There is an error in the submission.</dt>
              {Object.keys(errors.optional).map((errField) => (
                <ErrorMessage
                  key={errField}
                  errors={errors}
                  name={`optional.${errField}`}
                  as="dd"
                />
              ))}
            </dl>
          )}
        </div>
      </form>

      <ListResetDialog
        listId={list.id}
        isOpen={isDialogOpen}
        onClose={(goToStep1?: boolean) => {
          setIsDialogOpen(false)

          if (goToStep1) {
            navigate(`/${list.id}/form/${step1}`)
          }
        }}
      />
    </>
  )
}
